import { useCallback } from 'react';
import { baseNodeDefaults } from 'assets/js/pages/engage/workflows/utils/constants';
import { traverseDownTree } from 'assets/js/pages/engage/workflows/utils/helpers';
import { useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import { WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { v4 as uuid } from 'uuid';

interface UseCurrentNodeHandlerArgs {
    currentNode?: NodeWithData | null;
}

const useDeleteNodeHandler = ({ currentNode }: UseCurrentNodeHandlerArgs) => {
    const { nodes, edges, setNodes, setShouldLayout, takeSnapshot } = useReactFlowStore(
        ({ nodes, edges, setNodes, setShouldLayout, takeSnapshot }) => ({
            nodes,
            edges,
            setNodes,
            setShouldLayout,
            takeSnapshot,
        }),
    );

    const isBranchNode = currentNode?.type === WorkflowUtilityNodeTypeEnum.BRANCH;

    return useCallback(() => {
        if (!currentNode?.data) return;

        let newNodes: NodeWithData[];
        const topEdge = edges.find(edge => edge.target === currentNode.id);
        const bottomEdge = edges.find(edge => edge.source === currentNode.id);

        if (isBranchNode) {
            const nodesToDelete: string[] = [currentNode.id];
            traverseDownTree(nodes, currentNode.id, node => {
                nodesToDelete.push(node.id);
            });

            newNodes = [
                ...nodes.filter(node => !nodesToDelete.includes(node.id)),
                {
                    ...baseNodeDefaults,
                    id: uuid(),
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                        parentId: currentNode.data.parentId,
                    },
                    type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                },
            ];
        } else {
            newNodes = [
                ...nodes
                    .filter(node => node.id !== currentNode.id)
                    .map(node => {
                        if (node.id !== bottomEdge?.target || !node.data || !topEdge) return node;

                        return {
                            ...node,
                            data: {
                                ...node.data,
                                parentId: topEdge.source,
                            },
                        };
                    }),
            ];
        }

        takeSnapshot();
        setNodes(newNodes);
        setShouldLayout(true);
    }, [currentNode, isBranchNode, nodes, edges, setNodes, setShouldLayout, takeSnapshot]);
};

export { useDeleteNodeHandler };
